import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { Task } from '@/types';
import { RecoveryPlan, RecoveryStage, OverwhelmStatus } from '../types';
import { detectOverdueTasks, generateRecoveryPlan } from '../services/recoveryEngine';

interface RecoveryContextType {
    stage: RecoveryStage;
    overwhelmStatus: OverwhelmStatus | null;
    activePlan: RecoveryPlan | null;
    checkStatus: (tasks: Task[]) => void;
    startRecovery: (tasks: Task[]) => void;
    completeTask: (taskId: string) => void;
    exitRecovery: () => void;
}

const RecoveryContext = createContext<RecoveryContextType | undefined>(undefined);

export const RecoveryProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [stage, setStage] = useState<RecoveryStage>(RecoveryStage.IDLE);
    const [overwhelmStatus, setOverwhelmStatus] = useState<OverwhelmStatus | null>(null);
    const [activePlan, setActivePlan] = useState<RecoveryPlan | null>(null);

    // Check if the user needs saving
    const checkStatus = useCallback((tasks: Task[]) => {
        if (stage === RecoveryStage.ACTIVE) return; // Already running

        // Don't annoy the user if they recently dismissed it (could add logic here)
        const status = detectOverdueTasks(tasks);
        setOverwhelmStatus(status);

        if (status.isOverwhelmed && stage === RecoveryStage.IDLE) {
            setStage(RecoveryStage.DETECTION);
        } else if (!status.isOverwhelmed && stage === RecoveryStage.DETECTION) {
            setStage(RecoveryStage.IDLE);
        }
    }, [stage]);

    const startRecovery = useCallback((tasks: Task[]) => {
        // 1. Get the overwhelmed tasks
        const status = detectOverdueTasks(tasks);
        // 2. Build the plan
        // In a real app, we might ask the user for input (Triage Phase) before this
        const plan = generateRecoveryPlan("current-user", status.overdueTasks, status.upcomingCriticalTasks);

        setActivePlan(plan);
        setStage(RecoveryStage.ACTIVE);
    }, []);

    const completeTask = useCallback((taskId: string) => {
        setActivePlan((currentPlan) => {
            if (!currentPlan) return null;

            const newTasks = [...currentPlan.tasks];
            const currentIndex = currentPlan.currentStepIndex;

            // Mark current as done (logic would be more complex in real DB sync)
            // For local UI state:
            if (newTasks[currentIndex].id === taskId) {
                // Unlock next
                const nextIndex = currentIndex + 1;
                if (nextIndex < newTasks.length) {
                    newTasks[nextIndex].isLocked = false;
                    return {
                        ...currentPlan,
                        tasks: newTasks,
                        currentStepIndex: nextIndex
                    };
                } else {
                    // Victory!
                    setStage(RecoveryStage.COMPLETED);
                    return currentPlan;
                }
            }
            return currentPlan;
        });
    }, []);

    const exitRecovery = useCallback(() => {
        setStage(RecoveryStage.IDLE);
        setActivePlan(null);
        setOverwhelmStatus(null);
    }, []);

    return (
        <RecoveryContext.Provider value={{
            stage,
            overwhelmStatus,
            activePlan,
            checkStatus,
            startRecovery,
            completeTask,
            exitRecovery
        }}>
            {children}
        </RecoveryContext.Provider>
    );
};

export const useRecovery = () => {
    const context = useContext(RecoveryContext);
    if (context === undefined) {
        throw new Error('useRecovery must be used within a RecoveryProvider');
    }
    return context;
};
